import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Trophy, Star, CarProfile, ArrowLeft, Crown, Medal, Sparkle, Fire } from "@phosphor-icons/react";
import SteadyHandBadge from "@/components/SteadyHandBadge";

function MedalIcon({ rank }) {
  if (rank === 1) return <Crown size={20} weight="fill" className="text-primary" />;
  if (rank === 2) return <Medal size={20} weight="fill" className="text-foreground/70" />;
  if (rank === 3) return <Medal size={20} weight="duotone" className="text-foreground/50" />;
  return <span className="font-mono text-xs text-foreground/40">#{rank}</span>;
}

function FeaturedCard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/drivers/featured");
        setD(data);
      } catch {}
    })();
  }, []);
  if (!d) return null;
  return (
    <Link
      data-testid="featured-driver-card"
      to={`/d/${d.driver_id}`}
      className="block mt-8 border-2 border-foreground overflow-hidden group hover:translate-y-[-2px] transition-transform"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px]">
        {/* Left: name + bio + stats */}
        <div className="bg-foreground text-background p-8 relative">
          <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-primary text-foreground border-2 border-background px-2 py-1 font-mono text-[10px] uppercase tracking-wider">
            <Sparkle size={12} weight="fill" /> Driver of the Week
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60">
            {d.is_quiet_week ? "All-time leader · quiet week" : "This week's top driver"}
          </div>
          <div className="flex items-center gap-3 mt-3">
            {d.picture ? (
              <img src={d.picture} alt="" className="w-14 h-14 object-cover border-2 border-background" />
            ) : (
              <div className="w-14 h-14 bg-background text-foreground border-2 border-background flex items-center justify-center font-display text-2xl">
                {(d.name || "D")[0]}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-3xl tracking-tighter" data-testid="featured-name">{d.name}</h2>
                {d.verified && (
                  <span title="Verified" className="inline-flex items-center justify-center w-5 h-5 bg-accent text-background font-mono text-[10px]">✓</span>
                )}
              </div>
              <div className="text-background/70 flex items-center gap-1 text-sm mt-0.5">
                <CarProfile size={14} /> {d.vehicle}
              </div>
            </div>
          </div>
          {d.bio && (
            <p className="text-background/80 mt-4 max-w-xl text-sm leading-relaxed line-clamp-3">"{d.bio}"</p>
          )}
          <div className="grid grid-cols-4 gap-3 mt-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-background/60">Week score</div>
              <div className="font-display text-2xl text-primary" data-testid="featured-week-score">{d.weekly_score}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-background/60">Trips · 7d</div>
              <div className="font-display text-2xl">{d.recent_trips}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-background/60">Reviews · 7d</div>
              <div className="font-display text-2xl">{d.recent_comments}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-background/60">Rating</div>
              <div className="font-display text-2xl flex items-center gap-1">
                <Star size={20} weight="fill" className="text-primary" /> {d.rating_avg ? d.rating_avg.toFixed(1) : "—"}
              </div>
            </div>
          </div>
        </div>
        {/* Right: featured media or pattern */}
        <div className="relative bg-primary aspect-square md:aspect-auto md:min-h-[220px]">
          {d.featured_media ? (
            d.featured_media.kind === "image" ? (
              <img src={`${API}/public/media/${d.featured_media.storage_path}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <video src={`${API}/public/media/${d.featured_media.storage_path}`} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
            )
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-foreground">
              <Fire size={56} weight="duotone" />
              <div className="font-display text-2xl">On Fire</div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Leaderboard() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/drivers/leaderboard");
        setDrivers(data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-2 border-foreground bg-background sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2 font-display text-xl">
            <span className="inline-block w-3 h-3 bg-primary" />
            RideGY
          </Link>
          <Link to="/" className="font-mono text-xs uppercase tracking-wider inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 inline-flex items-center gap-2">
          <Trophy size={14} weight="duotone" /> Driver leaderboard
        </div>
        <h1 className="font-display text-5xl tracking-tighter mt-2">Guyana's top drivers</h1>
        <p className="text-foreground/70 max-w-2xl mt-2">
          Points = (completed trips × 10) + (sum of trip ratings) + (sum of review ratings). Higher = better.
        </p>

        <FeaturedCard />

        {/* Top 3 podium */}
        {drivers.length >= 3 && (
          <div className="mt-10 grid grid-cols-3 gap-3" data-testid="leaderboard-podium">
            {[1, 0, 2].map((idx, displayIdx) => {
              const d = drivers[idx];
              const rank = idx + 1;
              const heights = [200, 240, 170]; // medium tall short
              return (
                <Link
                  key={d.driver_id}
                  to={`/d/${d.driver_id}`}
                  className="flex flex-col items-stretch"
                >
                  <div className={`border-2 border-foreground p-4 ${rank === 1 ? "bg-primary" : "bg-card"} hover:translate-y-[-2px] transition-transform`}
                       style={{ minHeight: heights[displayIdx] }}>
                    <div className="flex items-center justify-between">
                      <MedalIcon rank={rank} />
                      {d.verified && (
                        <span className="inline-flex items-center justify-center w-4 h-4 bg-accent text-white font-mono text-[9px]">✓</span>
                      )}
                    </div>
                    <div className="mt-3 font-display text-xl truncate" data-testid={`podium-${rank}-name`}>{d.name}</div>
                    <div className="text-xs text-foreground/70 truncate">{d.vehicle}</div>
                    <div className="font-display text-3xl mt-3" data-testid={`podium-${rank}-points`}>{d.points}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">points</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Full list */}
        <div className="mt-10 border-2 border-foreground">
          {loading && <div className="p-8 font-mono text-xs uppercase tracking-wider">Loading…</div>}
          {!loading && drivers.length === 0 && (
            <div className="p-8 text-center text-foreground/60">No drivers yet.</div>
          )}
          {drivers.map((d, i) => (
            <Link
              key={d.driver_id}
              to={`/d/${d.driver_id}`}
              data-testid={`leaderboard-row-${d.driver_id}`}
              className={`flex items-center gap-4 p-4 ${i < drivers.length - 1 ? "border-b-2 border-foreground" : ""} hover:bg-secondary transition-colors`}
            >
              <div className="w-8 flex items-center justify-center">
                <MedalIcon rank={i + 1} />
              </div>
              {d.picture ? (
                <img src={d.picture} alt="" className="w-10 h-10 object-cover border-2 border-foreground" />
              ) : (
                <div className="w-10 h-10 bg-secondary border-2 border-foreground flex items-center justify-center font-display">
                  {(d.name || "D")[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate">{d.name}</span>
                  {d.verified && (
                    <span className="inline-flex items-center justify-center w-4 h-4 bg-accent text-white font-mono text-[9px]">✓</span>
                  )}
                  <SteadyHandBadge steady={d.steady_hand} size="sm" />
                </div>
                <div className="text-xs text-foreground/60 flex items-center gap-1">
                  <CarProfile size={12} /> {d.vehicle}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-foreground/70">
                <Star size={14} weight="fill" className="text-primary" />
                <span className="font-mono text-sm">{d.rating_avg ? d.rating_avg.toFixed(1) : "—"}</span>
                <span className="font-mono text-xs text-foreground/50">({d.rating_count})</span>
              </div>
              <div className="font-display text-2xl w-20 text-right" data-testid={`leaderboard-points-${d.driver_id}`}>
                {d.points}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
